import { GridApiPro } from "@mui/x-data-grid-pro";
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useExceptionEventsByRuleIds } from "@/hooks/exceptionEvent/useExceptionEventsByRuleIds";
import { useRulesByName } from "@/hooks/rules/useRulesByName";
import { DeviceStatusOverviewData } from "@/models/DeviceStatusOverviewData";
import { startBatteryRunawayAlarm, stopBatteryRunawayAlarm } from "@/utilities/alertSound";

/**
 * Placeholder rule name for the catastrophic "Battery Runaway" event. The real Geotab rule
 * does not exist yet, so this resolves to no events in production (safe). Swap for the real
 * rule name once it exists — single point of change. Test the alarm via {@link triggerTest}.
 */
export const BATTERY_RUNAWAY_RULE_NAMES = ["[BR] - Battery Runaway"];

export type BatteryRunawayPhase = "fullscreen" | "banner" | null;

interface AlarmState {
  phase: BatteryRunawayPhase;
  muted: boolean;
  deviceId: string | null;
  deviceName: string | null;
}

const IDLE: AlarmState = { phase: null, muted: false, deviceId: null, deviceName: null };

export interface UseBatteryRunawayAlarm {
  phase: BatteryRunawayPhase;
  muted: boolean;
  deviceName: string | null;
  /** Device id whose grid row should be marked while the alarm is active, else null. */
  alarmDeviceId: string | null;
  mute: () => void;
  goToDevice: () => void;
  acknowledge: () => void;
  /** DEV-only manual trigger for testing without a real event. */
  triggerTest: () => void;
}

export function useBatteryRunawayAlarm(
  data: DeviceStatusOverviewData[],
  apiRef: RefObject<GridApiPro>,
): UseBatteryRunawayAlarm {
  const { data: ruleResults } = useRulesByName(BATTERY_RUNAWAY_RULE_NAMES);
  const ruleIds = useMemo(
    () =>
      (ruleResults ?? [])
        .flat()
        .map(rule => rule.id)
        .filter((id): id is string => !!id),
    [ruleResults],
  );
  const { data: eventsByRule } = useExceptionEventsByRuleIds(
    ruleIds.length ? ruleIds : undefined,
  );
  const { activeSessionId } = useGeotabClient();

  const [state, setState] = useState<AlarmState>(IDLE);

  // Keep latest data accessible without retriggering the detection effect.
  const dataRef = useRef(data);
  dataRef.current = data;
  const resolveName = useCallback(
    (id: string | undefined | null) =>
      dataRef.current.find(d => d.device.id === id)?.device.name ?? id ?? null,
    [],
  );

  const fireAlarm = useCallback((deviceId: string | null, deviceName: string | null) => {
    setState({ phase: "fullscreen", muted: false, deviceId, deviceName });
  }, []);

  // Detect new events. Skip the first load so existing events don't fire on page open.
  const seenRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef(true);

  // On session switch the event queries refetch a different tenant. Reset detection so the new
  // tenant's existing events seed as "seen" instead of all firing, and clear any active alarm.
  useEffect(() => {
    seenRef.current = new Set();
    initialLoadRef.current = true;
    setState(IDLE);
  }, [activeSessionId]);

  useEffect(() => {
    if (!eventsByRule) return;
    const allEvents = Object.values(eventsByRule).flat();

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      allEvents.forEach(event => seenRef.current.add(event.id));
      return;
    }

    for (const event of allEvents) {
      if (seenRef.current.has(event.id)) continue;
      seenRef.current.add(event.id);
      fireAlarm(event.device?.id ?? null, resolveName(event.device?.id));
      break; // one alarm at a time
    }
  }, [eventsByRule, fireAlarm, resolveName]);

  // Siren plays only while the full-screen alarm is unmuted.
  useEffect(() => {
    if (state.phase === "fullscreen" && !state.muted) {
      startBatteryRunawayAlarm();
    } else {
      stopBatteryRunawayAlarm();
    }
    return () => stopBatteryRunawayAlarm();
  }, [state.phase, state.muted]);

  const mute = useCallback(() => setState(s => ({ ...s, muted: true })), []);

  const goToDevice = useCallback(() => {
    if (state.deviceName) {
      apiRef.current?.setFilterModel({ items: [], quickFilterValues: [state.deviceName] });
    }
    if (state.deviceId) {
      apiRef.current?.setExpandedDetailPanels([state.deviceId]);
    }
    // Collapse to a persistent banner so the grid stays usable; silence the siren.
    setState(s => ({ ...s, phase: "banner", muted: true }));
  }, [apiRef, state.deviceName, state.deviceId]);

  const acknowledge = useCallback(() => setState(IDLE), []);

  const triggerTest = useCallback(() => {
    const first = dataRef.current[0];
    fireAlarm(first?.device.id ?? "test-device", first?.device.name ?? "TEST-DEVICE");
  }, [fireAlarm]);

  return {
    phase: state.phase,
    muted: state.muted,
    deviceName: state.deviceName,
    alarmDeviceId: state.phase ? state.deviceId : null,
    mute,
    goToDevice,
    acknowledge,
    triggerTest,
  };
}
